<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200">
        <!-- Enhanced Loading State -->
        <div v-if="loading" class="tw-animate-pulse">
            <!-- Header Skeleton -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-64"></div>
                </div>
                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-40"></div>
            </div>
            
            <!-- Form Content Skeleton -->
            <div class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <!-- Left Column - Product Information Skeleton -->
                    <div class="tw-space-y-6">
                        <div>
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-40 tw-mb-4"></div>
                            
                            <!-- Title Field Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-2"></div>
                                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-1"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-64"></div>
                            </div>

                            <!-- Description Field Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-2"></div>
                                <div class="tw-h-32 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-1"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-80"></div>
                            </div>

                            <!-- Categories Field Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-mb-2"></div>
                                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-1"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-72"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Product Image Skeleton -->
                    <div class="tw-space-y-6">
                        <div>
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-4"></div>
                            
                            <!-- Image Upload Area Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-mb-2"></div>
                                <div class="tw-w-full tw-h-64 tw-bg-gray-200 tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center">
                                    <div class="tw-w-16 tw-h-16 tw-bg-gray-300 tw-rounded-full tw-mb-4"></div>
                                    <div class="tw-h-4 tw-bg-gray-300 tw-rounded tw-w-48 tw-mb-2"></div>
                                    <div class="tw-h-3 tw-bg-gray-300 tw-rounded tw-w-64 tw-mb-4"></div>
                                    <div class="tw-h-8 tw-bg-gray-300 tw-rounded tw-w-32"></div>
                                </div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-80 tw-mt-2"></div>
                            </div>

                            <!-- Image Tips Skeleton -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <div class="tw-h-4 tw-bg-blue-200 tw-rounded tw-w-24 tw-mb-2"></div>
                                <div class="tw-space-y-1">
                                    <div class="tw-h-3 tw-bg-blue-200 tw-rounded tw-w-64"></div>
                                    <div class="tw-h-3 tw-bg-blue-200 tw-rounded tw-w-56"></div>
                                    <div class="tw-h-3 tw-bg-blue-200 tw-rounded tw-w-72"></div>
                                    <div class="tw-h-3 tw-bg-blue-200 tw-rounded tw-w-60"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions Skeleton -->
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pt-8 tw-mt-8 tw-border-t tw-border-gray-200">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-36"></div>
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-40"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Edit Product</h1>
                    <p class="tw-text-sm tw-text-gray-600">Update information for "{{ originalProduct?.name || 'Loading...' }}"</p>
                </div>
                
                <!-- Back Button -->
                <a-button
                    @click="goBack"
                    intent="light"
                    left-icon="solar:arrow-left-linear"
                >
                    Back to Products
                </a-button>
            </div>

            <!-- Form Content -->
            <form @submit.prevent="updateProduct" class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <!-- Left Column - Product Information -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Information</h2>
                            
                            <!-- Product Title -->
                            <div class="tw-mb-6">
                                <FormField
                                    v-model="productForm.title"
                                    type="text"
                                    label="Product Title"
                                    placeholder="Enter product title"
                                    left-icon="solar:box-linear"
                                    :error="productErrors.title"
                                    required
                                    help="Enter a clear, descriptive title for your product"
                                />
                            </div>

                            <!-- Product Description -->
                            <div class="tw-mb-6">
                                <FormField
                                    v-model="productForm.description"
                                    type="textarea"
                                    label="Description"
                                    placeholder="Enter detailed product description"
                                    :error="productErrors.description"
                                    rows="6"
                                    help="Provide a detailed description of your product features and benefits"
                                />
                            </div>

                            <!-- Categories -->
                            <div class="tw-mb-6">
                                <FormMultiSelect
                                    v-model="productForm.categories"
                                    :options="categoryOptions"
                                    label="Categories"
                                    placeholder="Select product categories"
                                    left-icon="solar:widget-bold-duotone"
                                    :error="productErrors.categories"
                                    help="Choose relevant categories to help customers find your product"
                                    :loading="categoriesLoading"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Product Image -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Image</h2>
                            
                            <!-- Image Upload Area -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Product Image <span class="tw-text-gray-500">(Optional)</span>
                                </label>
                                
                                <div
                                    @drop.prevent="handleImageDrop"
                                    @dragover.prevent="isDragging = true"
                                    @dragleave.prevent="isDragging = false"
                                    @click="triggerImageInput"
                                    :class="[
                                        'tw-relative tw-w-full tw-h-64 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                        isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400',
                                        productForm.imagePreview ? 'tw-border-solid' : ''
                                    ]"
                                >
                                    <!-- Image Preview -->
                                    <div v-if="productForm.imagePreview" class="tw-relative tw-w-full tw-h-full">
                                        <img
                                            :src="productForm.imagePreview"
                                            alt="Product preview"
                                            class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                                        />
                                        <button
                                            @click.stop="removeImage"
                                            type="button"
                                            class="tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors"
                                        >
                                            <icon icon="solar:close-circle-bold" class="tw-text-lg" />
                                        </button>
                                    </div>
                                    
                                    <!-- Upload Placeholder -->
                                    <div v-else class="tw-text-center">
                                        <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400 tw-mb-4" />
                                        <p class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                            Drop an image here or click to upload
                                        </p>
                                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4">
                                            Supports JPG, PNG, GIF files up to 5MB
                                        </p>
                                        <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-100 tw-text-primary-700 tw-rounded-md tw-text-sm tw-font-medium">
                                            <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                            Choose Image
                                        </div>
                                    </div>
                                    
                                    <input
                                        ref="imageInput"
                                        type="file"
                                        accept="image/*"
                                        @change="handleImageSelect"
                                        class="tw-hidden"
                                    />
                                </div>
                                
                                <p class="tw-text-xs tw-text-gray-500 tw-mt-2">
                                    Recommended size: 800x600px. Maximum file size: 5MB
                                </p>
                            </div>

                            <!-- Image Upload Tips -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2">
                                    <icon icon="solar:info-circle-bold" class="tw-mr-1" />
                                    Image Tips
                                </h3>
                                <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                    <li>• Use high-quality images for better presentation</li>
                                    <li>• Square or landscape orientation works best</li>
                                    <li>• Ensure good lighting and clear product visibility</li>
                                    <li>• Avoid images with heavy text overlays</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pt-8 tw-mt-8 tw-border-t tw-border-gray-200">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Delete Button -->
                        <a-button
                            type="button"
                            intent="danger"
                            @click="deleteProduct"
                            :disabled="savingProduct"
                            left-icon="solar:trash-bin-2-bold-duotone"
                        >
                            Delete Product
                        </a-button>
                    </div>

                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Cancel Button -->
                        <a-button
                            type="button"
                            intent="white"
                            @click="goBack"
                            :disabled="savingProduct"
                        >
                            Cancel
                        </a-button>
                        
                        <!-- Update Product Button -->
                        <a-button
                            type="submit"
                            intent="primary"
                            :loading="savingProduct"
                            :disabled="!productForm.title || savingProduct"
                            left-icon="solar:pen-bold-duotone"
                            class="tw-min-w-[180px]"
                        >
                            Update Product
                        </a-button>
                    </div>
                </div>
            </form>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingProduct"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            title="Delete Product?"
            description="This action cannot be undone. The product will be permanently deleted."
        />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import FormField from '@/components/form/FormField.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import useAlert from '@/composables/useAlert'
import productApi from '@/api/products'
import appApi from '@/api/app'

const router = useRouter()
const route = useRoute()

// Form state
const loading = ref(true)
const categoriesLoading = ref(false)
const isDragging = ref(false)
const imageInput = ref(null)
const savingProduct = ref(false)
const deleteModalVisible = ref(false)
const deletingProduct = ref(false)
const originalProduct = ref(null)

// Product form
const productForm = reactive({
    title: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: []
})

const productErrors = reactive({
    title: '',
    description: '',
    categories: ''
})

// Categories data
const categories = ref([])

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

// Methods
const goBack = () => {
    router.push({ name: 'pages.company.products' })
}

const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('product')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        useAlert('Failed to load categories. Please try again.', 'warning')
        categories.value = []
    } finally {
        categoriesLoading.value = false
    }
}

const loadProduct = async () => {
    loading.value = true
    
    try {
        const productId = route.params.id
        const response = await productApi.edit(productId)
        
        if (response.data) {
            const product = response.data

            // Store original product data
            originalProduct.value = product

            // Populate form
            productForm.title = product.name || ''
            productForm.description = product.description || ''
            productForm.imagePreview = product.thumbnail_url || ''
            productForm.categories = product.categories || []
        } else {
            throw new Error('Product not found')
        }

    } catch (error) {
        console.error('Error loading product:', error)
        useAlert('Error loading product. Please try again.', 'danger')
        goBack()
    } finally {
        loading.value = false
    }
}

const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        processSelectedImage(file)
    }
}

const processSelectedImage = (file) => {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image file size must be less than 5MB', 'warning')
        return
    }

    productForm.image = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        productForm.imagePreview = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    productForm.image = null
    productForm.imagePreview = ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

const validateProductForm = () => {
    // Reset errors
    Object.keys(productErrors).forEach(key => {
        productErrors[key] = ''
    })

    let isValid = true

    // Title validation
    if (!productForm.title.trim()) {
        productErrors.title = 'Product title is required'
        isValid = false
    } else if (productForm.title.trim().length < 3) {
        productErrors.title = 'Product title must be at least 3 characters'
        isValid = false
    } else if (productForm.title.trim().length > 100) {
        productErrors.title = 'Product title must be less than 100 characters'
        isValid = false
    }

    // Description validation
    if (productForm.description.trim().length > 1000) {
        productErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    return isValid
}

const updateProduct = async () => {
    if (!validateProductForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingProduct.value = true

    try {
        const productId = route.params.id
        
        const formData = new FormData()
        formData.append('name', productForm.title.trim())
        formData.append('description', productForm.description.trim())
        
        if (productForm.image) {
            formData.append('thumbnail', productForm.image)
        }
        
        if (productForm.categories.length > 0) {
            formData.append('category_ids', JSON.stringify(productForm.categories.map(category => category.id)))
        }

        const response = await productApi.update(productId, formData)
        
        if (response.status === 200 || response.status === 201) {
            useAlert('Product updated successfully!', 'success')
            router.push({ name: 'pages.company.products' })
        } else {
            throw new Error('Failed to update product')
        }

    } catch (error) {
        console.error('Error updating product:', error)
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors
            Object.keys(serverErrors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(productErrors, key)) {
                    productErrors[key] = serverErrors[key][0]
                }
            })
            useAlert('Please fix the form errors', 'warning')
        } else {
            useAlert('Error updating product. Please try again.', 'danger')
        }
    } finally {
        savingProduct.value = false
    }
}

const deleteProduct = () => {
    deleteModalVisible.value = true
}

const confirmDelete = async () => {
    deletingProduct.value = true
    
    try {
        const productId = route.params.id
        const response = await productApi.destroy(productId)
        
        if (response.status === 200 || response.status === 204) {
            useAlert('Product deleted successfully!', 'success')
            router.push({ name: 'pages.company.products' })
        } else {
            throw new Error('Failed to delete product')
        }
        
    } catch (error) {
        console.error('Error deleting product:', error)
        
        if (error.response?.status === 404) {
            useAlert('Product not found or already deleted', 'warning')
            router.push({ name: 'pages.company.products' })
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to delete this product', 'danger')
        } else {
            useAlert('Error deleting product. Please try again.', 'danger')
        }
    } finally {
        deletingProduct.value = false
        deleteModalVisible.value = false
    }
}

// Initialize
onMounted(async () => {
    await Promise.all([
        fetchCategories(),
        loadProduct()
    ])
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 